import { DateTime } from 'luxon';

import { AudienceLogicTypes } from '@/common/enums/audience-logic-types';
import { AudienceRuleModes } from '@/common/enums/audience-rule-modes';
import AudienceGroup from '@/common/models/audience/AudienceGroup';
import { IAudienceNode } from '@/common/models/audience/AudienceNode';
import AudienceRuleCustomGroup from '@/common/models/audience/AudienceRuleCustomGroup';
import ExternalSegment from '@/common/models/audience/ExternalSegment';
import { DASH, FullDaypart } from '@/globals';
import Validatable, { IExtraValidation } from '@/validator/Validatable';

import AudienceLogic, { IAudienceLogicAPI } from './AudienceLogic';
import AudienceLookBackWindow, { IAudienceLookBackWindowAPI } from './AudienceLookBackWindow';

export interface IAudienceRuleAPI {
  audiencelookbackwindows?: IAudienceLookBackWindowAPI[];
  audiencelogic?: IAudienceLogicAPI;
  daypart: string;
  externaldemographic_id: number;
  geofencegroup_id: number;
  mode: string;
  name: string;
  customgroup_id: number;
  audiencelogic_id: number;
  key: number;
  audiencerule_id: number;
  timestamp?: string;
  audience_lookbackwindows_dict: {
    to_add: IAudienceLookBackWindowAPI[];
    to_update: Record<string, IAudienceLookBackWindowAPI>;
  };
  dataprovider_id: number;
}

export default class AudienceRule extends Validatable implements IAudienceNode {
  isNegated: boolean;
  type: AudienceLogicTypes;
  name?: string;
  isCustomDaypart: boolean;
  audienceRuleCustomGroup: AudienceRuleCustomGroup;

  constructor(
    public id: number = -1,
    public key?: number,
    public audienceLogicID?: number,
    public customGroupID?: number,
    name?: string,
    public mode?: AudienceRuleModes,
    public geofenceGroupID?: number,
    public externalDemographicID?: number,
    public daypart: string = FullDaypart,
    public audienceLogic?: AudienceLogic,
    public audienceLookbackWindows: AudienceLookBackWindow[] = [new AudienceLookBackWindow()],
    public timestamp: DateTime = DateTime.local(),
    public segments: ExternalSegment[] = [],
    public parent?: AudienceGroup,
    public dataProviderID?: number,
  ) {
    super();
    this.type = AudienceLogicTypes.Rule;
    this.isNegated = false;
    this.name = name;
    this.refreshIsCustomDaypart();
  }

  get segment(): ExternalSegment {
    return this.segments.length ? this.segments[0] : null;
  }

  get audienceLookbackWindow(): AudienceLookBackWindow {
    return this.audienceLookbackWindows !== undefined &&
      this.audienceLookbackWindows !== null &&
      this.audienceLookbackWindows.length > 0
      ? this.audienceLookbackWindows[0]
      : null;
  }

  get lookbackWindowDescription(): string {
    if (this.audienceLookbackWindow === null) {
      return DASH;
    }
    return this.audienceLookbackWindow.description;
  }

  get isDaypartFull(): boolean {
    return this.daypart !== null && this.daypart.indexOf('0') === -1;
  }

  get isAudienceDeviceIDGroup(): boolean {
    return (
      this.customGroupID > 0 ||
      (this.externalDemographicID == null && this.audienceLogicID == null && this.geofenceGroupID == null)
    );
  }

  refreshIsCustomDaypart(): void {
    this.isCustomDaypart = this.daypart != null ? this.daypart.localeCompare(FullDaypart) !== 0 : false;
  }

  static fromApi(apiObject: IAudienceRuleAPI): AudienceRule {
    if (apiObject.timestamp == null) {
      apiObject.timestamp = DateTime.local().toISODate();
    }

    return AudienceRule.fromObject({
      id: apiObject.audiencerule_id,
      type: AudienceLogicTypes.Rule,
      key: apiObject.key,
      audienceLogicID: apiObject.audiencelogic_id,
      customGroupID: apiObject.customgroup_id,
      name: apiObject.name,
      mode: apiObject.mode,
      geofenceGroupID: apiObject.geofencegroup_id,
      externalDemographicID: apiObject.externaldemographic_id,
      daypart: apiObject.daypart,
      audienceLogic: apiObject.audiencelogic,
      audienceLookbackWindows:
        apiObject.audiencelookbackwindows != null
          ? apiObject.audiencelookbackwindows.map((x) => AudienceLookBackWindow.fromApi(x))
          : null,
      timestamp: DateTime.fromISO(apiObject.timestamp),
      dataProviderID: apiObject.dataprovider_id,
    });
  }

  toApi(): IAudienceRuleAPI {
    let toAdd = [];
    const toUpdate = {};
    if (this.mode === AudienceRuleModes.Geofence || this.isAudienceDeviceIDGroup) {
      toAdd = this.audienceLookbackWindow.id === -1 ? [this.audienceLookbackWindow.toApi()] : [];
      if (this.audienceLookbackWindow.id !== -1) {
        toUpdate[this.audienceLookbackWindow.id] = this.audienceLookbackWindow.toApi();
      }
    }

    return {
      audiencerule_id: this.id >= 0 ? this.id : undefined,
      key: this.key,
      audiencelogic_id: this.audienceLogicID ? this.audienceLogicID : undefined,
      customgroup_id: this.customGroupID,
      name: this.name,
      mode: this.mode,
      geofencegroup_id: this.geofenceGroupID,
      externaldemographic_id: this.externalDemographicID,
      daypart: this.daypart,
      audience_lookbackwindows_dict: {
        to_add: toAdd,
        to_update: toUpdate,
      },
      dataprovider_id: this.dataProviderID,
    };
  }

  static fromObject(obj) {
    return super.fromObject(
      obj,
      new AudienceRule(
        obj.id,
        obj.key,
        obj.audienceLogicID,
        obj.customGroupID,
        obj.name,
        obj.mode,
        obj.geofenceGroupID,
        obj.externalDemographicID,
        obj.daypart,
        obj.audiencelogic,
        obj.audienceLookbackWindows,
        obj.timestamp,
        obj.segments,
        obj.parent,
        obj.dataProviderID,
      ),
    );
  }

  getExtraValidationRules() {
    const extraValidation: Record<string, IExtraValidation> = {};

    if (this.mode === AudienceRuleModes.Geofence) {
      return {
        ...extraValidation,
        geofenceGroupID: { value: this.geofenceGroupID, rules: 'required' },
      };
    }
    if (this.mode === AudienceRuleModes.Demographic && !this.isAudienceDeviceIDGroup) {
      return {
        ...extraValidation,
        externalDemographicID: { value: this.externalDemographicID, rules: 'required' },
      };
    }

    return extraValidation;
  }

  static validatorCreateRules() {
    const rules: Record<string, string> = {
      key: 'required|numeric:int',
      name: 'required|max:250|not_empty_or_whitespace',
    };

    return rules;
  }
}
